import { useState } from 'react';
import { EvidenceButton, EvidenceLines, type Lines } from '@/components/profile/restitution/Evidence';
import type { QualificationBrief } from '@shared/types/company-restitution';

const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '');

/** Level 1: the answer, readable in a minute — the diagnosis, three findings, what to do first, what to decide. */
export const BriefView = ({ brief, lines, stale, deepThemes }: { brief: QualificationBrief; lines: Lines; stale: boolean; deepThemes: number }) => {
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (key: string) => setOpen(open === key ? null : key);
  return (
    <section className="rounded-xl border border-domino-cardBorder bg-domino-warm px-6 py-6 sm:px-8">
      <p className="font-display text-2xl leading-snug text-domino-ink">{brief.headline}</p>
      <p className="mt-2 text-xs text-domino-inkSoft">
        Rédigée le {formatDate(brief.writtenAt)} · {lines.size} constat{lines.size > 1 ? 's' : ''} sourcé{lines.size > 1 ? 's' : ''}
        {deepThemes > 0 ? ` · ${deepThemes} thème${deepThemes > 1 ? 's' : ''} approfondi${deepThemes > 1 ? 's' : ''}` : ''}
        {stale && <span className="ml-2 text-amber-800">La fiche a changé depuis.</span>}
      </p>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {brief.findings.map((finding, index) => (
          <div key={`${finding.title}-${index}`} className="rounded-lg bg-white/70 px-4 py-3">
            <EvidenceButton ids={finding.evidence} lines={lines} open={open === `f${index}`} onToggle={() => toggle(`f${index}`)} />
            <p className="mt-2 font-semibold leading-6 text-domino-ink">{finding.title}</p>
            {finding.detail && <p className="mt-1 text-sm text-domino-inkSoft">{finding.detail}</p>}
            {open === `f${index}` && <EvidenceLines ids={finding.evidence} lines={lines} className="mt-2" />}
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {brief.steps.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-domino-ink">Ce qu’on fait d’abord</h3>
            <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm leading-6 text-domino-ink">
              {brief.steps.map((step, index) => (
                <li key={`${step.text}-${index}`}>{step.text}{step.when && <span className="text-domino-inkSoft"> · {step.when}</span>}</li>
              ))}
            </ol>
          </div>
        )}
        {brief.decisions.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-domino-ink">À trancher par vous</h3>
            <ul className="mt-2 space-y-2 text-sm leading-6 text-domino-ink">
              {brief.decisions.map((decision, index) => (
                <li key={`${decision.text}-${index}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span>{decision.text}</span>
                    <EvidenceButton ids={decision.evidence} lines={lines} open={open === `d${index}`} onToggle={() => toggle(`d${index}`)} />
                  </div>
                  {open === `d${index}` && <EvidenceLines ids={decision.evidence} lines={lines} className="mt-1" />}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};
